import { getItem } from "../utils/localStorage";
import {jwtDecode } from "jwt-decode";

const Permissions = {
  // Dashboard permissions
  DASHBOARD_VIEW: "dashboard:view",
  DASHBOARD_MESSAGING_TRENDS_VIEW: "dashboard:messaging_trends:view",
  DASHBOARD_BOT_VISITORS_VIEW: "dashboard:bot_visitors:view",

  // Reporting permissions
  REPORTS_VIEW: "reports:view",
  REPORTS_DOWNLOAD: "reports:download",

  // Modeler permissions
  MODELER_PROCESS_CREATE: "modeler:process:create",
  MODELER_NODE_CREATE: "modeler:node:create",
  MODELER_NODE_EDIT: "modeler:node:edit",
  MODELER_MODEL_SAVE: "modeler:model:save",
  MODELER_MODEL_EXPORT: "modeler:model:export",
  MODELER_MODEL_IMPORT: "modeler:model:import",
  MODELER_FUNCTION_CREATE: "modeler:function:create",

  // Integration permissions
  INTEGRATIONS_CHANNELS_VIEW: "integrations:channels:view",
  INTEGRATIONS_CHANNELS_EDIT: "integrations:channels:edit",
  INTEGRATIONS_INBOUND_VIEW: "integrations:inbound:view",
  INTEGRATIONS_INBOUND_EDIT: "integrations:inbound:edit",
  INTEGRATIONS_OUTBOUND_VIEW: "integrations:outbound:view",
  INTEGRATIONS_OUTBOUND_EDIT: "integrations:outbound:edit",
  INTEGRATIONS_LLM_VIEW: "integrations:llm:view",
  INTEGRATIONS_LLM_EDIT: "integrations:llm:edit",

  // Message permissions
  MESSAGES_LOGS_VIEW: "messages:logs:view",
  MESSAGES_BOT_TRAIN: "messages:bot:train",
  MESSAGES_RESPONSE_VIEW: "messages:response:view",

  // Tool permissions
  TOOLS_CHAT_SIMULATOR_USE: "tools:chat_simulator:use",
  TOOLS_ERROR_LOGS_VIEW: "tools:error_logs:view",
  TOOLS_OPENAI_SETUP: "tools:openai:setup",
  TOOLS_BROADCAST_SEND: "tools:broadcast:send",

  // Settings permissions
  SETTINGS_USERS_MANAGE: "settings:users:manage",
  SETTINGS_ROLES_MANAGE: "settings:roles:manage",
  SETTINGS_CONFIG_MANAGE: "settings:config:manage",

  // Parent permissions (for role assignments)
  ADMIN_ALL: "admin:all",
  DASHBOARD_MANAGE: "dashboard:manage",
  MODELER_ADMIN: "modeler:admin",
  INTEGRATIONS_ADMIN: "integrations:admin",
  REPORTS_MANAGE: "reports:manage",
  SETTINGS_ADMIN: "settings:admin",

  // Permission inheritance mapping (parent → children)
  HIERARCHY: {
    // Wildcard permission
    "admin:all": ["*"],

    // Dashboard domain
    "dashboard:manage": [
      "dashboard:view",
      "dashboard:messaging_trends:view",
      "dashboard:bot_visitors:view"
    ],

    // Modeler domain
    "modeler:admin": [
      "modeler:process:create",
      "modeler:node:create",
      "modeler:node:edit",
      "modeler:model:save",
      "modeler:model:export",
      "modeler:model:import",
      "modeler:function:create"
    ],

    // Integrations domain
    "integrations:admin": [
      "integrations:channels:view",
      "integrations:channels:edit",
      "integrations:inbound:view",
      "integrations:inbound:edit",
      "integrations:outbound:view",
      "integrations:outbound:edit",
      "integrations:llm:view",
      "integrations:llm:edit"
    ],

    // Reporting domain
    "reports:manage": [
      "reports:view",
      "reports:download"
    ],

    // Settings domain
    "settings:admin": [
      "settings:users:manage",
      "settings:roles:manage",
      "settings:config:manage"
    ]
  },

  /**
   * Get all defined permissions including parent permissions.
   * Used for UI elements or API seeding.
   * @returns {string[]} Array of all permission strings
   */
  allPermissions() {
    return Object.values(this)
      .filter(value => typeof value === 'string' && value.includes(':'))
      .sort();
  },

  /**
   * Get all concrete permissions (excluding parent permissions).
   * Used for wildcard resolution and system checks.
   * @returns {Set<string>} Set of leaf permissions
   */
  allLeafPermissions() {
    const leafPermissions = new Set();

    for (const children of Object.values(this.HIERARCHY)) {
      for (const perm of children) {
        if (perm !== '*' && !this.HIERARCHY[perm]) {
          leafPermissions.add(perm);
        }
      }
    }

    return leafPermissions;
  },

  /**
   * Expand permissions to include all implied child permissions.
   * @param {string[]} permissions - List of direct permissions assigned to user/role
   * @returns {Set<string>} Set of all effective permissions including inherited ones
   */
  resolve(permissions) {
    const expanded = new Set();

    for (const perm of permissions) {
      // Wildcard permission short-circuit
      if (perm === '*') {
        return this.allLeafPermissions();
      }

      expanded.add(perm);

      // Add all child permissions from hierarchy
      if (this.HIERARCHY[perm]) {
        for (const childPerm of this.HIERARCHY[perm]) {
          expanded.add(childPerm);
        }
      }
    }

    return expanded;
  },

  /**
   * Check if user has the required permission (including inheritance).
   * @param {string[]} userPermissions - List of permissions assigned to user
   * @param {string} required - Permission being checked
   * @returns {boolean} True if user has effective permission
   */
  checkPermission(userPermissions, required) {
    const expanded = this.resolve(userPermissions);
    return expanded.has(required) || expanded.has('*');
  },

  /**
   * Get the user's permissions from the JWT token's _features key.
   * @returns {string[]} Array of user permissions
   */
  getUserPermissions() {
    try {
      const token = getItem("access_token");
      if (!token) {
        return [];
      }
      const decoded = jwtDecode(token);
      const permissions = decoded._features || [];
      return Array.isArray(permissions) ? permissions : [];
    } catch (err) {
      return [];
    }
  }
};

export default Permissions;